import { useState, useCallback, type FormEvent } from "react";
import { Link } from "react-router-dom";

export default function Kontakt() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    setFormStatus("idle");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("company_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      setFormLoading(false);
      form.reset();
      return;
    }

    formData.delete("company_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, value.toString());
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/d9ssom14ne17ei0sdnbg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam") || serverMsg.toLowerCase().includes("form data is spam");

      if (!response.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setFormStatus("error");
        setFormError(serverMsg || "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.");
      } else {
        setFormStatus("success");
        form.reset();
      }
    } catch (err) {
      setFormStatus("error");
      setFormError("Ein Netzwerkfehler ist aufgetreten. Bitte versuchen Sie es später erneut.");
    } finally {
      setFormLoading(false);
    }
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Page Header */}
      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4 md:mb-6">
              <Link to="/" className="hover:text-primary-600 transition-colors">Startseite</Link>
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line" /></span>
              <span className="text-foreground-700">Kontakt</span>
            </nav>
            <h1 className="font-heading font-bold text-foreground-900 text-3xl md:text-4xl lg:text-5xl mb-3 md:mb-4">
              Kontakt
            </h1>
            <p className="text-foreground-600 max-w-2xl text-sm md:text-base">
              Nehmen Sie mit uns Kontakt auf – wir beraten Sie gerne persönlich zu Ihrem Projekt.
            </p>
          </div>
        </div>
      </section>

      {/* Contact info + form */}
      <section className="py-10 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Contact info */}
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <div>
                <h2 className="font-heading font-semibold text-foreground-900 text-lg md:text-xl mb-3 md:mb-4">
                  Unsere Adresse
                </h2>
                <address className="not-italic text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p className="font-medium text-foreground-800 mb-1">
                    Draude & Wagner Galvanik<br />
                    GmbH & Co. KG
                  </p>
                  <p>Im Grund 11</p>
                  <p>58513 Lüdenscheid</p>
                </address>
              </div>

              <div>
                <h2 className="font-heading font-semibold text-foreground-900 text-lg md:text-xl mb-3 md:mb-4">
                  Erreichbarkeit
                </h2>
                <ul className="space-y-2.5 text-sm md:text-base text-foreground-600">
                  <li className="flex items-center gap-2.5">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line" />
                    </span>
                    <span>+49 2351 123456</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line" />
                    </span>
                    <span>info@dw-galvanik.de</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500 flex-shrink-0">
                      <i className="ri-global-line" />
                    </span>
                    <span>www.dw-galvanik.de</span>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading font-semibold text-foreground-900 text-lg md:text-xl mb-3 md:mb-4">
                  Öffnungszeiten
                </h2>
                <ul className="space-y-1.5 text-sm md:text-base text-foreground-600">
                  <li className="flex justify-between max-w-[200px]">
                    <span>Mo – Do</span>
                    <span>07:00 – 16:00 Uhr</span>
                  </li>
                  <li className="flex justify-between max-w-[200px]">
                    <span>Freitag</span>
                    <span>07:00 – 13:00 Uhr</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact form */}
            <div className="lg:col-span-3">
              <div className="bg-background-50 rounded-lg p-5 md:p-8 border border-background-200/70">
                <h2 className="font-heading font-semibold text-foreground-900 text-lg md:text-xl mb-4 md:mb-6">
                  Nachricht senden
                </h2>

                {formStatus === "success" && (
                  <div className="mb-4 md:mb-6 p-3 md:p-4 bg-primary-50 border border-primary-200 rounded-md text-primary-700 text-sm">
                    <span className="w-4 h-4 flex items-center justify-center inline-block mr-1">
                      <i className="ri-check-line" />
                    </span>
                    Vielen Dank! Ihre Nachricht wurde erfolgreich übermittelt. Wir melden uns zeitnah bei Ihnen.
                  </div>
                )}

                {formStatus === "error" && (
                  <div className="mb-4 md:mb-6 p-3 md:p-4 bg-accent-50 border border-accent-200 rounded-md text-accent-700 text-sm">
                    <span className="w-4 h-4 flex items-center justify-center inline-block mr-1">
                      <i className="ri-error-warning-line" />
                    </span>
                    {formError || "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut."}
                  </div>
                )}

                <form onSubmit={handleSubmit} data-readdy-form="d9ssom14ne17ei0sdnbg" className="space-y-3 md:space-y-4">
                  {/* Honeypot */}
                  <div className="hp-field" aria-hidden="true">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      aria-hidden="true"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-3 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                        placeholder="Ihr Name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                        placeholder="ihre@email.de"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-3 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                        placeholder="+49 1234 567890"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1">
                        Betreff *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-3 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                        placeholder="Worum geht es?"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs md:text-sm font-medium text-foreground-700 mb-1">
                      Nachricht *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-3 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
                      placeholder="Ihre Nachricht an uns..."
                    />
                    <p className="text-xs text-foreground-500 mt-1">Max. 500 Zeichen</p>
                  </div>

                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="privacy"
                      name="privacy"
                      required
                      className="mt-0.5 w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400"
                    />
                    <label htmlFor="privacy" className="text-xs text-foreground-600 leading-relaxed">
                      Ich habe die{" "}
                      <Link to="/datenschutz" className="text-primary-600 hover:text-primary-700 underline">
                        Datenschutzerklärung
                      </Link>{" "}
                      gelesen und stimme der Verarbeitung meiner Daten zu. *
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={formLoading}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap text-sm md:text-base"
                  >
                    {formLoading ? (
                      <>
                        <span className="w-4 h-4 flex items-center justify-center mr-2 animate-spin">
                          <i className="ri-loader-4-line" />
                        </span>
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        Nachricht senden
                        <span className="w-4 h-4 flex items-center justify-center ml-2">
                          <i className="ri-send-plane-line" />
                        </span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="py-10 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-semibold text-foreground-900 text-lg md:text-xl mb-4 md:mb-6">
              So finden Sie uns
            </h2>
            <div className="rounded-lg overflow-hidden border border-background-200/70 aspect-[16/9] md:aspect-[21/9]">
              <iframe
                src="https://maps.google.com/maps?width=100%25&height=600&hl=de&q=Im%20Grund%2011%2C%2058513%20L%C3%BCdenscheid&t=&z=15&ie=UTF8&iwloc=B&output=embed"
                title="Google Maps - Draude & Wagner Galvanik GmbH & Co. KG, Im Grund 11, 58513 Lüdenscheid"
                className="w-full h-full border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="mt-3 md:mt-4 text-xs md:text-sm text-foreground-500 flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line" />
              </span>
              Draude & Wagner Galvanik GmbH & Co. KG, Im Grund 11, 58513 Lüdenscheid
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}